import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Package, LogOut, ChevronRight, User as UserIcon } from "lucide-react";
import { format } from "date-fns";
import Ticker from "@/components/Ticker";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  processing: "bg-blue-100 text-blue-700",
  shipped: "bg-purple-100 text-purple-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
  refunded: "bg-muted text-muted-foreground",
};

const AccountPage = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });
    return () => subscription.unsubscribe();
  }, []);

  const { data: orders = [], isLoading: ordersLoading } = useQuery({
    queryKey: ["my-orders", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("*")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);

  const { data: orderItems } = useQuery({
    queryKey: ["my-order-items", expandedOrder],
    queryFn: async () => {
      if (!expandedOrder) return [];
      const { data, error } = await supabase
        .from("order_items")
        .select("*")
        .eq("order_id", expandedOrder);
      if (error) throw error;
      return data;
    },
    enabled: !!expandedOrder,
  });

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
      <SEO title="My Account" description="View your Plymhair order history, track deliveries and manage your account details." url="/account" />
        <Ticker />
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading…</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!user) {
    navigate("/auth");
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <SEO title="My Account" description="View your Plymhair order history, track deliveries and manage your account details." url="/account" />
      <Ticker />
      <Navbar />

      <div className="container py-8 flex-1">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold">
              My Account
            </h1>
            <p className="text-sm text-muted-foreground font-body mt-1">
              {user.email}
            </p>
          </div>
          <Button variant="outline" onClick={handleLogout} className="gap-2">
            <LogOut className="w-4 h-4" />
            Sign Out
          </Button>
        </div>

        {/* Orders */}
        <section>
          <h2 className="font-display text-lg font-semibold mb-4">
            Order History
          </h2>

          {ordersLoading ? (
            <div className="text-muted-foreground text-center py-12">
              Loading orders…
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-16 bg-card border border-border rounded-lg">
              <Package className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
              <p className="text-muted-foreground font-body mb-4">
                You haven't placed any orders yet
              </p>
              <Link to="/shop">
                <Button>Start Shopping</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((order) => (
                <div
                  key={order.id}
                  className="bg-card border border-border rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() =>
                      setExpandedOrder(
                        expandedOrder === order.id ? null : order.id
                      )
                    }
                    className="w-full flex items-center gap-4 p-4 hover:bg-secondary/30 transition-colors text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-sm font-medium">
                          #{order.id.slice(0, 8)}
                        </span>
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded ${
                            statusColors[order.status] ||
                            "bg-muted text-muted-foreground"
                          }`}
                        >
                          {order.status.charAt(0).toUpperCase() +
                            order.status.slice(1)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground font-body mt-1">
                        {format(
                          new Date(order.created_at),
                          "dd MMM yyyy, HH:mm"
                        )}
                      </p>
                    </div>
                    <span className="font-body font-bold text-sm">
                      £{Number(order.total).toFixed(2)}
                    </span>
                    <ChevronRight
                      className={`w-4 h-4 text-muted-foreground transition-transform ${
                        expandedOrder === order.id ? "rotate-90" : ""
                      }`}
                    />
                  </button>

                  {expandedOrder === order.id && (
                    <div className="border-t border-border p-4 bg-secondary/10">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-display font-semibold text-sm mb-3">
                            Items
                          </h4>
                          {orderItems?.length ? (
                            <div className="space-y-2">
                              {orderItems.map((item) => (
                                <div
                                  key={item.id}
                                  className="flex items-center gap-3 bg-card rounded p-2 border border-border"
                                >
                                  {item.product_image && (
                                    <img
                                      src={item.product_image}
                                      alt=""
                                      className="w-12 h-12 rounded object-cover bg-muted"
                                    />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-body font-medium truncate">
                                      {item.product_name}
                                    </p>
                                    {item.variant_label && (
                                      <p className="text-xs text-muted-foreground">
                                        {item.variant_label}
                                      </p>
                                    )}
                                  </div>
                                  <p className="text-sm font-body text-muted-foreground whitespace-nowrap">
                                    {item.quantity} × £
                                    {Number(item.unit_price).toFixed(2)}
                                  </p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground animate-pulse">
                              Loading items…
                            </p>
                          )}
                        </div>
                        <div>
                          <h4 className="font-display font-semibold text-sm mb-3">
                            Details
                          </h4>
                          <dl className="space-y-1.5 text-sm font-body">
                            <div className="flex justify-between">
                              <dt className="text-muted-foreground">Subtotal</dt>
                              <dd>
                                £{Number(order.subtotal).toFixed(2)}
                              </dd>
                            </div>
                            <div className="flex justify-between font-bold">
                              <dt>Total</dt>
                              <dd>£{Number(order.total).toFixed(2)}</dd>
                            </div>
                          </dl>
                          {order.shipping_address && (
                            <div className="mt-4">
                              <p className="text-xs text-muted-foreground mb-1 font-body">
                                Shipping Address
                              </p>
                              <p className="text-sm font-body whitespace-pre-line">
                                {order.shipping_address}
                              </p>
                            </div>
                          )}

                          {/* Tracking info for shipped orders */}
                          {(order.status === "shipped" ||
                            order.status === "delivered") && (
                            <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                              <p className="text-xs text-primary font-body font-bold uppercase mb-1">
                                {order.status === "shipped"
                                  ? "🚚 In Transit"
                                  : "✅ Delivered"}
                              </p>
                              <p className="text-sm text-muted-foreground font-body">
                                {order.status === "shipped"
                                  ? "Your order is on its way! You'll receive a delivery update soon."
                                  : "Your order has been delivered. We hope you love it!"}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default AccountPage;
